import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import {
  fetchDataBuilderResults,
  verifyDataBuilderPayload,
} from '../../api/data-builder-api';
import { withDataBuilderMutationPolicy } from '../../api/data-builder-mutation-policy';
import { useBuildDataBuilderPayload } from '../../api/useBuildDataBuilderPayload';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import { useCompanySelectionGate } from '../../CompanySelectionGateContext';
import {
  selectDeliverableClient,
  selectDeliverableName,
  selectDeliveryConfigured,
  useDataBuilderSelector,
} from '../../data-builder.store';

const FALLBACK_ERROR_MESSAGE = 'Validation failed.';

const isValidationErrorDetail = (
  detail: unknown
): detail is { msg: string }[] => {
  if (!Array.isArray(detail) || detail.length === 0) {
    return false;
  }
  return detail.every(
    (item) =>
      item !== null &&
      typeof item === 'object' &&
      'msg' in item &&
      typeof (item as { msg: unknown }).msg === 'string'
  );
};

const extractErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error) || error.message.length === 0) {
    return FALLBACK_ERROR_MESSAGE;
  }
  try {
    const parsed: unknown = JSON.parse(error.message);
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      !('detail' in parsed)
    ) {
      return error.message;
    }
    const detail = (parsed as { detail: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (isValidationErrorDetail(detail)) {
      return detail.map((e) => e.msg).join(' ');
    }
  } catch {
    // not JSON, fall through to raw message
  }
  return error.message;
};

type UseVerifyDataBuilderResult = {
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  isReady: boolean;
  isWaitingForCompanies: boolean;
};

export const useVerifyDataBuilder = (): UseVerifyDataBuilderResult => {
  const { kibanaLogger } = useKibanaLogger();
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });

  const { status: companySelectionGateStatus } = useCompanySelectionGate();

  const deliveryConfigured = useDataBuilderSelector(selectDeliveryConfigured);
  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);

  const trimmedName = storedName?.trim() ?? '';
  const payloadName = trimmedName.length > 0 ? trimmedName : 'Data Builder';
  const payloadClient = storedClient !== '' ? storedClient : null;

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
  });

  const { mutate, isPending, isError, isSuccess, error } = useMutation(
    withDataBuilderMutationPolicy(
      {
        mutationKey: ['data-builder', 'verify'],
        mutationFn: verifyDataBuilderPayload,
      },
      kibanaLogger
    )
  );

  const buildRef = useRef(build);
  buildRef.current = build;

  const payload = rawSchema !== undefined ? buildRef.current(rawSchema) : null;
  const payloadSignature = payload === null ? null : JSON.stringify(payload);

  useEffect(() => {
    if (
      rawSchema === undefined ||
      !deliveryConfigured ||
      companySelectionGateStatus === 'pending' ||
      payloadSignature === null
    ) {
      return;
    }
    const next = buildRef.current(rawSchema);
    if (next === null) {
      return;
    }
    mutate(next);
  }, [
    payloadSignature,
    rawSchema,
    deliveryConfigured,
    companySelectionGateStatus,
    mutate,
  ]);

  const errorMessage = extractErrorMessage(error);

  const isWaitingForCompanies = companySelectionGateStatus === 'pending';

  return {
    isPending,
    isError,
    errorMessage,
    isReady: isSuccess && !isPending && !isError && !isWaitingForCompanies,
    isWaitingForCompanies,
  };
};
